/**
 * Web Push (tarayıcı bildirimi) yardımcıları — Ayarlar > Bildirimler "bu cihazda push" akışı.
 *
 * Gereksinim: aktif Service Worker (yalnız PROD'da kayıtlı — bkz. pwa.ts) + HTTPS/localhost + izin.
 * Dev'de SW kayıtlı olmadığından push aboneliği YAPILAMAZ (helper null döner, UI bunu bildirir).
 */

/** Tarayıcı push'u destekliyor mu (SW + PushManager + Notification API). */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Mevcut bildirim izni durumu ("default" | "granted" | "denied"), desteklenmiyorsa "unsupported". */
export function notificationPermission(): NotificationPermission | "unsupported" {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

/** VAPID açık anahtarını (base64url) pushManager.subscribe için Uint8Array'e çevir.
 *  ArrayBuffer üzerinden kurulur → `BufferSource` (applicationServerKey) ile tip-uyumlu. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const arr = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Aktif SW kaydını verir. Kayıt YOKSA (dev / henüz register olmamış) null döner —
 * `navigator.serviceWorker.ready`'yi kayıt yokken beklemek SONSUZ askıda kalır, o yüzden önce getRegistration.
 */
async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (!existing) return null;
    // Kayıt varsa aktif olana kadar bekle (kısa timeout ile askıda kalmayı önle).
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration | null>((resolve) => setTimeout(() => resolve(existing.active ? existing : null), 4000)),
    ]);
  } catch {
    return null;
  }
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** Mevcut aboneliğin endpoint'i (bu cihaz zaten abone mi). */
export async function currentSubscriptionEndpoint(): Promise<string | null> {
  const reg = await activeRegistration();
  if (!reg) return null;
  try {
    const sub = await reg.pushManager.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
}

/**
 * Bildirim izni ister ve push aboneliği oluşturur (varsa mevcut aboneliği döndürür).
 * İzin verilmezse / SW yoksa null döner. Dönen veri backend'e (subscribePush) gönderilmeli.
 */
export async function subscribeToPush(publicKey: string): Promise<PushSubscriptionData | null> {
  if (!pushSupported()) return null;
  const reg = await activeRegistration();
  if (!reg) return null;

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return null;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/** Bu cihazın aboneliğini iptal eder; iptal edilen endpoint'i döndürür (backend'den de silinsin). */
export async function unsubscribeFromPush(): Promise<string | null> {
  const reg = await activeRegistration();
  if (!reg) return null;
  try {
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
  } catch {
    return null;
  }
}
